// Re:Vault status page. Polls /status.json and renders the live service health
// plus the current online-user count. Everything is read-only; the page just
// reflects what the server reports and re-fetches on a light interval.
import { mountLogo } from "../shared/brand.js";
import { gardenText, STAGES } from "../shared/commons.js";

const logoEl = document.getElementById("rv-status-logo");
if (logoEl) mountLogo(logoEl);

// When the status page is open INSIDE the app (as a BiB tab), the brand / back
// links must NOT navigate this iframe to "/" - that would load a whole second
// copy of Re:Vault nested inside the tab (the duplicate-BiB bug). Instead, ask
// the app to close this status tab, which returns focus to the app. When the
// page is opened standalone (top-level) the links keep their normal href.
if (window.parent && window.parent !== window) {
	const backHome = (e) => {
		e.preventDefault();
		try { window.parent.postMessage({ type: "revault-close" }, location.origin); } catch (err) {}
	};
	document.querySelectorAll(".rv-status-brand, .rv-status-back").forEach((a) => a.addEventListener("click", backHome));
}

const overallEl = document.getElementById("rv-status-overall");
const overallText = document.getElementById("rv-status-overall-text");
const onlineEl = document.getElementById("rv-status-online");
const onlineNum = document.getElementById("rv-status-online-num");
const listEl = document.getElementById("rv-status-list");
const updatedEl = document.getElementById("rv-status-updated");
const buildEl = document.getElementById("rv-status-build");
const gardenEl = document.getElementById("rv-status-garden");

// The Commons garden: this week's stage and rounded hours, and how far last week's grew.
function gardenLine(c) {
	if (!c || typeof c !== "object") return "";
	const now = gardenText(c) || (Number.isInteger(c.s) ? "Commons garden: just planted this week" : "");
	const l = c.last && Number.isInteger(c.last.s) ? " Last week it reached " + STAGES[Math.min(c.last.s, STAGES.length - 1)].toLowerCase() + "." : "";
	return now ? now + "." + l : "";
}

const OVERALL = {
	operational: { label: "All systems operational", cls: "ok" },
	degraded: { label: "Partial service disruption", cls: "warn" },
	down: { label: "Major outage", cls: "down" },
	unknown: { label: "Checking status…", cls: "warn" },
};
const COMP = {
	operational: { label: "Operational", cls: "ok" },
	degraded: { label: "Degraded", cls: "warn" },
	down: { label: "Down", cls: "down" },
};

function fmtCount(n) { return typeof n === "number" && n >= 0 ? n.toLocaleString() : "—"; }
function fmtWhen(iso) {
	try { const d = new Date(iso); if (isNaN(d.getTime())) return ""; return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
	catch (e) { return ""; }
}

function render(data) {
	const ov = OVERALL[data && data.overall] || OVERALL.unknown;
	overallEl.dataset.state = ov.cls;
	overallText.textContent = ov.label;

	onlineNum.textContent = fmtCount(data && data.online);
	onlineEl.dataset.state = "ok";

	if (gardenEl) { gardenEl.textContent = gardenLine(data && data.commons); gardenEl.hidden = !gardenEl.textContent; }

	listEl.textContent = "";
	(data && Array.isArray(data.components) ? data.components : []).forEach((c) => {
		const meta = COMP[c.status] || COMP.down;
		const rowEl = document.createElement("div");
		rowEl.className = "rv-status-row";
		const name = document.createElement("span");
		name.className = "rv-status-name";
		name.textContent = c.name || c.id || "Component";
		const badge = document.createElement("span");
		badge.className = "rv-status-badge";
		badge.dataset.state = meta.cls;
		const dot = document.createElement("span");
		dot.className = "rv-status-dot";
		const bl = document.createElement("span");
		bl.textContent = meta.label;
		badge.append(dot, bl);
		rowEl.append(name, badge);
		listEl.appendChild(rowEl);
	});

	if (updatedEl) updatedEl.textContent = data && data.updatedAt ? "Last checked " + fmtWhen(data.updatedAt) : "";
	if (buildEl) buildEl.textContent = data && data.build && data.build.id ? "Build " + data.build.id : "";
}

function renderError() {
	overallEl.dataset.state = "down";
	overallText.textContent = "Can’t reach the status service";
	onlineNum.textContent = "—";
	listEl.textContent = "";
	if (updatedEl) updatedEl.textContent = "Retrying…";
}

let timer = null;
async function poll() {
	try {
		const r = await fetch("/status.json", { cache: "no-store" });
		if (!r.ok) throw new Error("bad status");
		render(await r.json());
	} catch (e) { renderError(); }
}
function start() { if (timer) return; poll(); timer = setInterval(poll, 15000); }
function stop() { if (timer) { clearInterval(timer); timer = null; } }
// Pause polling when the tab is hidden (saves work on low-end devices), resume on show.
document.addEventListener("visibilitychange", () => { if (document.hidden) stop(); else start(); });
start();
